import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../ui';
import { ToolIcon, type IconId } from './DiagramTools';

/**
 * Rahmen des Vollbildmodus im Skizzeneditor ("Gross bearbeiten"): eine eigene Ebene ueber allem (am `body`, im Druck unsichtbar).
 * Der Rahmen kennt nur die Anordnung; Zustand und Inhalte kommen vom `DiagramEditor` als Platzhalter-Inhalte (Slots), damit es derselbe Editor
 * bleibt - Elemente, Auswahl und Rueckgaengig-Verlauf gehen beim Wechsel nicht verloren.
 *
 * Breit (ab `md`): Kopfzeile, links die senkrechte Werkzeugleiste, in der Mitte das Feld so gross wie moeglich, rechts eine Spalte mit
 * Hinweis, Auswahl und Eigenschaften. Schmal (Handy): Kopfzeile, das Feld darunter, ein einklappbares Blatt fuer die Auswahl und
 * die Werkzeugleiste fest unten (Daumen, unter Beruecksichtigung der Sicherheitszone).
 *
 * Die Ebene liegt auf derselben Stufe wie die Dialoge (`z-50`) und kommt im Dokument nach ihnen; Dialoge, die aus dem Vollbild heraus
 * aufgehen (Aufbau einfuegen), werden noch spaeter angehaengt und liegen dadurch ueber ihr.
 */

/** Breite Anordnung ab Tailwind `md` (768 px). Ohne `matchMedia` (Tests) gilt der breite Bildschirm. */
export const WIDE_QUERY = '(min-width: 768px)';

export function useWideScreen(): boolean {
  const read = (): boolean => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(WIDE_QUERY).matches : true);
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(WIDE_QUERY);
    const update = (): void => setWide(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return wide;
}

export interface BoxSize {
  width: number;
  height: number;
}

/** Misst einen Kasten (Groesse des Inhaltsbereichs) und meldet Aenderungen; ohne ResizeObserver (Tests) bleibt es `null`. */
export function useBoxSize(): [BoxSize | null, (node: HTMLElement | null) => void] {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [size, setSize] = useState<BoxSize | null>(null);
  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return;
    const measure = (): void => {
      const style = window.getComputedStyle(node);
      const pad = (a: string, b: string): number => (Number.parseFloat(a) || 0) + (Number.parseFloat(b) || 0);
      const width = node.clientWidth - pad(style.paddingLeft, style.paddingRight);
      const height = node.clientHeight - pad(style.paddingTop, style.paddingBottom);
      setSize((current) => (current && current.width === width && current.height === height ? current : { width, height }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  const ref = useCallback((next: HTMLElement | null) => setNode(next), []);
  return [size, ref];
}

/** Groesstes Feld mit dem Seitenverhaeltnis `ratio` (Breite durch Hoehe), das in den Kasten passt; `null` solange er nicht messbar ist. */
export function fitBox(box: BoxSize | null, ratio: number): BoxSize | null {
  if (!box || box.width < 40 || box.height < 40 || !(ratio > 0)) return null;
  const width = Math.floor(Math.min(box.width, box.height * ratio));
  return { width, height: Math.floor(width / ratio) };
}

/** Knopf der Kopfzeile: Symbol, der Name nur auf breiten Bildschirmen (der Name bleibt immer als Beschriftung fuer Hilfsmittel). */
export function HeaderButton({
  label,
  icon,
  showText,
  disabled,
  expanded,
  controls,
  onClick,
}: {
  label: string;
  icon: IconId;
  showText: boolean;
  disabled?: boolean;
  expanded?: boolean;
  controls?: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      aria-controls={controls}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border px-2.5 text-sm font-medium shadow-sm transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-45',
        showText ? '' : 'min-w-11 px-0',
        expanded ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
      )}
    >
      <ToolIcon id={icon} className="h-4 w-4" />
      {showText && <span>{label}</span>}
    </button>
  );
}

export interface ExpandedShellProps {
  wide: boolean;
  title: string;
  onClose: () => void;
  /** Rueckgaengig und Wiederherstellen. */
  history: ReactNode;
  /** Zoomsteuerung in der Kopfzeile (nur breit). */
  zoom?: ReactNode;
  /** Knoepfe "Ansicht" und "Bedienung". */
  menu: ReactNode;
  /** Aufgeklapptes Feld unter der Kopfzeile ("Ansicht" oder "Bedienung"). */
  popover?: ReactNode;
  /** Die Werkzeugleiste (breit senkrecht, schmal waagerecht). */
  toolbar: ReactNode;
  /** Das Feld; `fieldRef` misst den Platz, den es fuellen darf. */
  field: ReactNode;
  fieldRef: (node: HTMLElement | null) => void;
  /** Werkzeug, Elementzaehler und Hinweiszeile. */
  status: ReactNode;
  /** Breit: Inhalt der rechten Spalte unter dem Hinweis. Schmal: Inhalt des Blatts. */
  side: ReactNode;
  /** Schmal: Beschriftung des Blatt-Knopfs; `null` blendet das Blatt aus. */
  sheetLabel?: string | null;
  sheetOpen: boolean;
  onToggleSheet: () => void;
  sheetId: string;
}

export function ExpandedShell({
  wide,
  title,
  onClose,
  history,
  zoom,
  menu,
  popover,
  toolbar,
  field,
  fieldRef,
  status,
  side,
  sheetLabel,
  sheetOpen,
  onToggleSheet,
  sheetId,
}: ExpandedShellProps): JSX.Element {
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="diagram-expanded"
      data-layout={wide ? 'wide' : 'narrow'}
      className="fixed inset-0 z-50 flex flex-col bg-slate-50 print:hidden"
    >
      <header className="relative flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]" data-testid="expanded-header">
        <h2 className="mr-auto hidden truncate text-base font-semibold text-slate-900 md:block">Skizze</h2>
        <div className="mr-auto flex items-center gap-1.5 md:mr-0">{history}</div>
        {wide && zoom}
        <div className="flex items-center gap-1.5">{menu}</div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex min-h-11 items-center justify-center rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
        >
          Fertig
        </button>
        {popover && (
          <div
            data-testid="expanded-popover"
            className="absolute right-2 top-full z-10 mt-1 max-h-[calc(100dvh-5rem)] w-[min(26rem,calc(100vw-1rem))] overflow-y-auto rounded-xl border border-slate-300 bg-white p-2 shadow-lg"
          >
            {popover}
          </div>
        )}
      </header>

      {wide ? (
        <div className="flex min-h-0 flex-1">
          <aside aria-label="Werkzeugspalte" data-testid="expanded-tools" className="w-[5.5rem] shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-2">
            {toolbar}
          </aside>
          <main ref={fieldRef} data-testid="expanded-stage" className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden p-3">
            {field}
          </main>
          <aside aria-label="Auswahl und Hinweise" data-testid="expanded-side" className="w-72 shrink-0 space-y-3 overflow-y-auto border-l border-slate-200 bg-white p-3 lg:w-80">
            {status}
            {side}
          </aside>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <main ref={fieldRef} data-testid="expanded-stage" className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden p-1">
            {field}
          </main>
          <div className="shrink-0 px-3 pb-1">{status}</div>
          {sheetLabel !== null && sheetLabel !== undefined && (
            <section aria-label="Auswahl" data-testid="expanded-sheet" className="shrink-0 border-t border-slate-200 bg-white">
              <button
                type="button"
                aria-expanded={sheetOpen}
                aria-controls={sheetId}
                onClick={onToggleSheet}
                className="flex min-h-11 w-full items-center justify-between gap-2 px-3 text-left text-sm font-semibold text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/60"
              >
                <span>{sheetLabel}</span>
                <svg viewBox="0 0 16 16" aria-hidden="true" className={cn('h-4 w-4 shrink-0 transition-transform', sheetOpen ? 'rotate-90' : '-rotate-90')}>
                  <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {sheetOpen && (
                <div id={sheetId} data-testid="expanded-sheet-body" className="max-h-[40dvh] space-y-3 overflow-y-auto px-3 pb-3 [&_[data-testid=selection-count]]:hidden [&_[data-testid=selection-title]]:hidden">
                  {side}
                </div>
              )}
            </section>
          )}
          <div data-testid="expanded-tools" className="shrink-0 border-t border-slate-200 bg-white px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {toolbar}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

/** Knopf "Groß bearbeiten" neben Rückgängig/Wiederherstellen im normalen Editor: Aussehen wie die Aktionsknöpfe der Leiste. */
export function ExpandButton({ onClick, buttonRef, iconOnly = false }: { onClick: () => void; buttonRef: { current: HTMLButtonElement | null }; iconOnly?: boolean }): JSX.Element {
  return (
    <button
      ref={(node) => {
        buttonRef.current = node;
      }}
      type="button"
      aria-label="Groß bearbeiten"
      title="Skizze bildschirmfüllend bearbeiten"
      data-testid="expand-editor"
      onClick={onClick}
      className={cn(iconOnly ? 'min-w-11 px-0' : 'px-2 sm:px-2.5', 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white py-1 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 sm:text-sm')}
    >
      <ToolIcon id="expand" className="h-4 w-4" />
      {!iconOnly && <span>Groß bearbeiten</span>}
    </button>
  );
}
